import { KeyRound } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  Input,
} from '@aeris/ui'

import { ApiError, confirmPerson } from '../lib/api.ts'

/**
 * "É você mesmo?" — a senha da pessoa escolhida (F298).
 *
 * No PDV, escolher outra pessoa como vendedora pede a senha DELA, na sessão
 * de quem está logado: "vai que tô em outro PC até eu logar". Só a senha — a
 * pessoa já foi escolhida na lista. A senha sai da memória assim que é usada.
 */
export const ConfirmPersonDialog = ({
  userId,
  displayName,
  onConfirmed,
  onClose,
}: {
  userId: string
  displayName: string
  onConfirmed: (userId: string) => void
  onClose: () => void
}): ReactNode => {
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  const submit = async (): Promise<void> => {
    setPending(true)
    setError(null)
    try {
      await confirmPerson({ userId, password })
      setPassword('')
      onConfirmed(userId)
    } catch (failure) {
      setPassword('')
      setError(
        failure instanceof ApiError
          ? failure.message
          : 'Não foi possível conferir a senha.'
      )
    } finally {
      setPending(false)
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        title={`Senha de ${displayName}`}
        description={`Para a venda sair no nome de ${displayName}, ela digita a senha dela. Quem está logado continua o mesmo.`}
      >
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            if (password !== '' && !pending) void submit()
          }}
        >
          <Field label={`Senha de ${displayName}`}>
            {(field) => (
              <Input
                {...field}
                type="password"
                autoComplete="off"
                autoFocus
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value)
                }}
              />
            )}
          </Field>
          {error === null ? null : (
            <p role="alert" className="text-(length:--text-sm) text-(--danger)">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={password === '' || pending}
              leadingIcon={<KeyRound aria-hidden="true" className="h-4 w-4" />}
            >
              {pending ? 'Conferindo…' : 'Confirmar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
